<template>
  <div class="psk-page flex h-screen min-h-0 w-full flex-col overflow-hidden text-white antialiased md:flex-row">
    <PlusAppSidebar />

    <div class="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#000]">
      <main class="relative z-10 mx-auto w-full max-w-[1320px] flex-1 overflow-y-auto px-4 py-7 sm:px-7 lg:px-10">
        <!-- 顶栏 -->
        <header class="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div class="min-w-0">
            <h1 class="text-[28px] font-extrabold tracking-tight text-white sm:text-[32px]">技能</h1>
            <nav class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[14px]">
              <button
                type="button"
                class="psk-cat"
                :class="{ 'psk-cat--active': activeCat === 'mine' }"
                @click="activeCat = 'mine'"
              >
                我的技能
              </button>
              <span class="text-white/25 select-none" aria-hidden="true">|</span>
              <button
                v-for="c in publicCats"
                :key="c.key"
                type="button"
                class="psk-cat"
                :class="{ 'psk-cat--active': activeCat === c.key }"
                @click="activeCat = c.key"
              >
                {{ c.label }}
              </button>
            </nav>
          </div>

          <button type="button" class="psk-create shrink-0 self-start" @click="openCreate">
            <i class="fas fa-plus text-[12px]"></i>
            创建我的 Skill
          </button>
        </header>

        <!-- 加载 -->
        <div v-if="loading" class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <div
            v-for="i in 8"
            :key="'sk-' + i"
            class="animate-pulse overflow-hidden rounded-2xl bg-[#161616]"
          >
            <div class="aspect-[16/10] bg-white/[0.06]"></div>
            <div class="space-y-3 p-4">
              <div class="h-4 w-2/3 rounded bg-white/[0.08]"></div>
              <div class="h-3 w-full rounded bg-white/[0.05]"></div>
              <div class="h-9 rounded-lg bg-white/[0.06]"></div>
            </div>
          </div>
        </div>

        <!-- 空态 -->
        <div
          v-else-if="!filteredSkills.length"
          class="mt-20 text-center text-[14px] text-[#888]"
        >
          <p v-if="activeCat === 'mine'">还没有收藏或自建 Skill，先去「全部」试试看吧</p>
          <p v-else>该分类暂无技能</p>
        </div>

        <!-- 网格 -->
        <section
          v-else
          class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          <PlusSkillsMarketCard
            v-for="card in filteredSkills"
            :key="card.id"
            :card="card"
            :bookmarked="isBookmarked(card.id)"
            @try="onTry"
            @toggle-bookmark="toggleBookmark"
          />
        </section>
      </main>
    </div>

    <!-- 创建弹层 -->
    <div
      v-if="createOpen"
      class="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4"
      @click.self="createOpen = false"
    >
      <div class="w-full max-w-md rounded-2xl border border-white/10 bg-[#141414] p-5 shadow-2xl">
        <h2 class="text-lg font-bold text-white">创建我的 Skill</h2>
        <p class="mt-1 text-[12px] text-[#888]">保存在本机，可在「我的技能」中查看</p>
        <label class="mt-4 block text-[12px] text-[#aaa]">名称</label>
        <input
          v-model.trim="draft.title"
          class="mt-1.5 w-full rounded-xl border border-white/10 bg-[#1c1c1c] px-3 py-2.5 text-sm text-white outline-none focus:border-pink-500/50"
          maxlength="40"
          placeholder="例如：故事板做视频"
        />
        <label class="mt-3 block text-[12px] text-[#aaa]">简介</label>
        <textarea
          v-model.trim="draft.desc"
          rows="3"
          class="mt-1.5 w-full resize-none rounded-xl border border-white/10 bg-[#1c1c1c] px-3 py-2.5 text-sm text-white outline-none focus:border-pink-500/50"
          maxlength="120"
          placeholder="一句话说明这个 Skill 能做什么"
        ></textarea>
        <label class="mt-3 block text-[12px] text-[#aaa]">默认提示词（可选）</label>
        <textarea
          v-model.trim="draft.prompt"
          rows="3"
          class="mt-1.5 w-full resize-none rounded-xl border border-white/10 bg-[#1c1c1c] px-3 py-2.5 text-sm text-white outline-none focus:border-pink-500/50"
          maxlength="500"
          placeholder="点击「试试看」时带入创作台"
        ></textarea>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm text-[#aaa] hover:text-white"
            @click="createOpen = false"
          >
            取消
          </button>
          <button type="button" class="psk-create !px-5 !py-2 text-sm" @click="saveCreate">
            保存
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PlusAppSidebar from '../components/PlusAppSidebar.vue'
import PlusSkillsMarketCard from '../components/PlusSkillsMarketCard.vue'
import { fetchSkillsCatalog, mapSkillItem } from '../utils/bannerApi'

const BOOKMARK_KEY = 'plus_skill_bookmarks'
const MY_SKILLS_KEY = 'plus_my_skills'
const LAUNCH_KEY = 'plus_skill_launch'

const PUBLIC_CATS = [
  { key: 'all', label: '全部' },
  { key: '自媒体', label: '自媒体' },
  { key: '广告营销', label: '广告营销' },
  { key: '游戏', label: '游戏' },
  { key: '周边设计', label: '周边设计' }
]

const DEMO_SKILLS = [
  {
    id: 'demo-storyboard',
    title: '故事板做视频',
    desc: '把分镜草图一键变成可拍的短视频脚本与画面',
    category: '自媒体',
    image:
      'https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=800&q=80',
    prompt: '根据故事板分镜生成短视频脚本与画面提示词'
  },
  {
    id: 'demo-worldcup',
    title: '我在世界杯现场',
    desc: '沉浸式球场氛围，生成你在看台的第一视角片段',
    category: '游戏',
    image:
      'https://images.unsplash.com/photo-1522778119026-d647f0596c20?auto=format&fit=crop&w=800&q=80',
    prompt: '第一人称世界杯看台现场，欢呼与烟火氛围'
  },
  {
    id: 'demo-brawl',
    title: '世界杯大乱斗',
    desc: '夸张卡通足球大乱斗，适合短视频开场',
    category: '游戏',
    image:
      'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=800&q=80',
    prompt: '卡通风格世界杯大乱斗，夸张动作与特效'
  },
  {
    id: 'demo-drone',
    title: '无人机航拍',
    desc: '城市与海岸线的航拍运镜提示词套装',
    category: '自媒体',
    image:
      'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=800&q=80',
    prompt: '无人机航拍城市日落，平滑推进与环绕运镜'
  },
  {
    id: 'demo-fruit',
    title: '水果短剧',
    desc: '拟人化水果角色，一键生成短剧分集创意',
    category: '自媒体',
    image:
      'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?auto=format&fit=crop&w=800&q=80',
    prompt: '拟人化香蕉与西瓜主演的轻松水果短剧'
  },
  {
    id: 'demo-bilibili',
    title: 'B站百大共创-万物拟人化短片',
    desc: '万物拟人化共创风格，适合二创与联名',
    category: '周边设计',
    image:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80',
    prompt: '万物拟人化短片，轻松幽默的共创风格'
  },
  {
    id: 'demo-pet',
    title: '萌宠故事',
    desc: '戴厨师帽的猫主厨，温馨治愈系宠物短片',
    category: '周边设计',
    image:
      'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&w=800&q=80',
    prompt: '萌宠厨师猫的温馨治愈故事短片'
  },
  {
    id: 'demo-ad',
    title: '品牌种草脚本',
    desc: '30 秒种草口播结构，适配广告投放素材',
    category: '广告营销',
    image:
      'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=800&q=80',
    prompt: '30秒品牌种草短视频脚本，突出卖点与行动号召'
  }
].map((item, i) => mapSkillItem(item, i))

function inferCategory(item) {
  const raw = String(item.category || item.type || item.tag || item.group || '').trim()
  if (PUBLIC_CATS.some((c) => c.key === raw)) return raw
  const text = `${item.title || ''} ${item.desc || ''} ${item.prompt || ''}`
  if (/广告|营销|种草|投放|品牌/.test(text)) return '广告营销'
  if (/游戏|世界杯|乱斗|电竞|关卡/.test(text)) return '游戏'
  if (/周边|设计|拟人|萌宠|联名|百大/.test(text)) return '周边设计'
  if (/短剧|短视频|航拍|故事|自媒体|分镜|视频/.test(text)) return '自媒体'
  return '自媒体'
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    return parsed != null ? parsed : fallback
  } catch (e) {
    return fallback
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    /* ignore */
  }
}

export default {
  name: 'PlusSkills',
  components: { PlusAppSidebar, PlusSkillsMarketCard },
  data() {
    return {
      loading: true,
      skills: [],
      mySkills: [],
      bookmarks: [],
      activeCat: 'all',
      publicCats: PUBLIC_CATS,
      createOpen: false,
      draft: { title: '', desc: '', prompt: '' }
    }
  },
  computed: {
    allCatalog() {
      const mine = (this.mySkills || []).map((s) => ({ ...s, _mine: true }))
      const remote = (this.skills || []).map((s) => ({ ...s, _mine: false }))
      const ids = new Set(mine.map((s) => s.id))
      return [...mine, ...remote.filter((s) => !ids.has(s.id))]
    },
    filteredSkills() {
      if (this.activeCat === 'mine') {
        const bookIds = new Set(this.bookmarks)
        return this.allCatalog.filter((s) => s._mine || bookIds.has(s.id))
      }
      if (this.activeCat === 'all') return this.allCatalog.filter((s) => !s._mine)
      return this.allCatalog.filter((s) => !s._mine && s.category === this.activeCat)
    }
  },
  mounted() {
    this.bookmarks = readJson(BOOKMARK_KEY, [])
    if (!Array.isArray(this.bookmarks)) this.bookmarks = []
    this.mySkills = readJson(MY_SKILLS_KEY, [])
    if (!Array.isArray(this.mySkills)) this.mySkills = []
    this.loadSkills()
  },
  methods: {
    async loadSkills() {
      this.loading = true
      try {
        const { skills, source } = await fetchSkillsCatalog()
        const list = Array.isArray(skills) ? skills : []
        this.skills = (list.length ? list : DEMO_SKILLS).map((s) => ({
          ...s,
          category: inferCategory(s),
          thumbGradient: s.thumbGradient
        }))
        if (!list.length && process.env.NODE_ENV === 'development') {
          console.info('[PlusSkills] 使用演示技能数据')
        } else if (process.env.NODE_ENV === 'development') {
          console.info('[PlusSkills] source=', source, 'count=', list.length)
        }
      } catch (e) {
        this.skills = DEMO_SKILLS.map((s) => ({ ...s, category: inferCategory(s) }))
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PlusSkills]', e)
        }
      } finally {
        this.loading = false
      }
    },
    isBookmarked(id) {
      return this.bookmarks.includes(String(id))
    },
    toggleBookmark(card) {
      const id = String(card.id)
      const set = new Set(this.bookmarks.map(String))
      if (set.has(id)) set.delete(id)
      else set.add(id)
      this.bookmarks = Array.from(set)
      writeJson(BOOKMARK_KEY, this.bookmarks)
    },
    onTry(card) {
      const url = card && card.linkUrl ? String(card.linkUrl).trim() : ''
      if (url && /^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }
      const prompt = (card && (card.prompt || card.desc || card.title)) || ''
      const payload = {
        id: card && card.id,
        title: card && card.title,
        prompt,
        systemPrompt: (card && card.systemPrompt) || '',
        modelId: (card && card.modelId) || '',
        modelKind: (card && card.modelKind) || ''
      }
      try {
        sessionStorage.setItem(LAUNCH_KEY, JSON.stringify(payload))
      } catch (e) {
        /* ignore */
      }
      const params = new URLSearchParams()
      if (card && card.id) params.set('skill', String(card.id))
      if (prompt) params.set('prompt', prompt)
      if (payload.modelId) params.set('model', payload.modelId)
      const q = params.toString() ? `?${params.toString()}` : ''
      this.$router.push(`/workspace${q}`).catch(() => {})
    },
    openCreate() {
      this.draft = { title: '', desc: '', prompt: '' }
      this.createOpen = true
    },
    saveCreate() {
      if (!this.draft.title) return
      const item = mapSkillItem(
        {
          id: `mine-${Date.now()}`,
          title: this.draft.title,
          desc: this.draft.desc || '我创建的 Skill',
          prompt: this.draft.prompt || this.draft.desc || this.draft.title,
          category: '自媒体',
          image: ''
        },
        this.mySkills.length
      )
      item.category = '自媒体'
      item._mine = true
      this.mySkills = [item, ...this.mySkills]
      writeJson(MY_SKILLS_KEY, this.mySkills)
      this.createOpen = false
      this.activeCat = 'mine'
    }
  }
}
</script>

<style scoped>
.psk-cat {
  border: 0;
  background: transparent;
  padding: 0;
  color: #8a8a8a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;
}

.psk-cat:hover {
  color: #d4d4d4;
}

.psk-cat--active {
  color: #fff;
  font-weight: 700;
}

.psk-create {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  padding: 10px 18px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(90deg, #ff2d7a 0%, #e84dff 100%);
  box-shadow: 0 8px 24px rgba(232, 77, 255, 0.25);
  transition: filter 0.15s ease, transform 0.15s ease;
}

.psk-create:hover {
  filter: brightness(1.06);
  transform: translateY(-1px);
}
</style>
